import { mapGetters, mapActions } from 'vuex';
const _get = require('lodash.get');
const _uniq = require('lodash.uniq');

import { ProviderMixin } from '../mixins/provider';
import { TimeHelper } from '../mixins/time';
import { GAME_FILTER_DEFAULT } from '../components/shared/queueup/queueup-constants';

export const QueueupApi = {
  mixins: [ProviderMixin, TimeHelper],
  computed: {
    ...mapGetters('app', ['appConfig']),
    ...mapGetters('user', ['userData']),
  },
  created() {
    const LS_KEY_GAMES = 'queueup/consumer-landing/games';
    const LS_KEY_TRAITS = 'queueup/consumer-landing/traits';

    const isLoggedIn = this.appConfig.auth_check;
    const userId = _get(this, 'userData.id');
    const self = this;

    this.ConsumerLandingApi = {
      readGames: async function() {
        let games;
        if (isLoggedIn) {
          games = await self.apiGetConsumerGames();
        } else {
          games = getItemFromLocalStorage(LS_KEY_GAMES);
        }
        games = Array.isArray(games) ? games.filter(e => Object.keys(self.allowedGames).includes(e)) : [];

        // if the user has already selected games, saved in DB
        // clear out localStorage cache to prevent potential malfunctioning of redirects
        if (isLoggedIn && games.length > 0) {
          localStorage.removeItem(LS_KEY_GAMES);
        }
        self.updateUserQueueupActiveGames(_uniq(games));
        return (games.length > 0) ? _uniq(games) : null;
      },
      saveGames: async function(games) {
        self.updateUserQueueupActiveGames(_uniq(games));
        if (isLoggedIn) {
          return self.apiSaveConsumerGames(userId, _uniq(games));
        } else {
          localStorage.setItem(LS_KEY_GAMES, JSON.stringify(games));
        }
      },
      saveTraits: async function(traits) {
        if (isLoggedIn) {
          return self.apiSaveConsumerTraits(userId, traits);
        } else {
          localStorage.setItem(LS_KEY_TRAITS, JSON.stringify(traits));
        }
      },
      // save games & traits to backend once user signs in / up
      saveSelectionOnAuth: async function(userId) {
        const games = getItemFromLocalStorage(LS_KEY_GAMES);
        const traits = getItemFromLocalStorage(LS_KEY_TRAITS);

        if (games) {
          await self.apiSaveConsumerGames(userId, games);
        }
        if (traits) {
          await self.apiSaveConsumerTraits(userId, traits);
        }

        localStorage.removeItem(LS_KEY_GAMES);
        localStorage.removeItem(LS_KEY_TRAITS);
      },
    };
  },

  methods: {
    ...mapActions({
      updateUserExtraData: 'user/updateExtraData',
    }),
    // ///////////////////////
    updateUserQueueupActiveGames(games) {
      if (games.length === 0 && !this.appConfig.auth_check) {
        games = GAME_FILTER_DEFAULT;
      }
      var listActiveGames = [];
      for (let i = 0; i < games.length; i++) {
        listActiveGames.push({ game: games[i] });
      }

      var userExtraDataUpdate = {
        loaded_queueup_active_games: true,
        queueup_active_games: listActiveGames,
      };

      this.updateUserExtraData(userExtraDataUpdate);
    },
    // ///////////////////////
    async apiGetConsumerGames() {
      const url = `${this.appConfig.baseUrl}/api/queueup-get-consumer-games?user_id=${this.userData.id}`;
      try {
        const response = await this.$http.get(url);
        const list = _get(response, 'data.list', []);
        return list.map(e => e.game);
      } catch (e) {
        this.providerAPIResponseErrorHandler(e);
        return [];
      }
    },
    // ////////////////////////
    async apiSaveConsumerGames(userId, games) {
      const url = `${this.appConfig.baseUrl}/api/queueup-add-consumer-games`;
      const payload = {
        user_id: userId,
        games,
      };

      try {
        const response = await this.$http.post(url, payload);
        return response.data;
      } catch (e) {
        this.providerAPIResponseErrorHandler(e);
        return null;
      }
    },
    // /////////////////////////
    async apiSaveConsumerTraits(userId, traits) {
      const url = `${this.appConfig.baseUrl}/api/queueup-add-consumer-traits`;
      const payload = {
        user_id: userId,
        traits,
      };

      try {
        const response = await this.$http.post(url, payload);
        return response.data;
      } catch (e) {
        this.providerAPIResponseErrorHandler(e);
        return null;
      }
    },
    // ///////////////////////////////
    async apiGetRecommendedProviders({ cardOffset = 0, cardLimit = 3, servicesLimit = 3, tagsLimit = 4, games = [] } = {}) {
      const url = `${this.appConfig.baseUrl}/api/consumer/recommended-providers`;
      const payload = {
        cardOffset,
        cardLimit,
        servicesLimit,
        tagsLimit,
        gamesList: JSON.stringify(games),
        ...this.getTimeZoneOffsetSecondsPayload(),
      };

      try {
        const response = await this.$http.post(url, payload);
        return response.data.recommendedProviders;
      } catch (e) {
        this.providerAPIResponseErrorHandler(e);
        throw e;
      }
    },
    // ///////////////////////
    async apiGetTopProviders({ cardOffset = 0, cardLimit = 4, tagsLimit = 2, games = [] } = {}) {
      const url = `${this.appConfig.baseUrl}/api/consumer/top-providers`;
      const payload = {
        cardOffset,
        cardLimit,
        tagsLimit,
        gamesList: JSON.stringify(games),
        ...this.getTimeZoneOffsetSecondsPayload(),
      };

      try {
        const response = await this.$http.post(url, payload);
        return response.data;
      } catch (e) {
        this.providerAPIResponseErrorHandler(e);
        throw e;
      }
    },
  },
};

// local storage helper to read specific key
function getItemFromLocalStorage(localStorageKey) {
  let value;
  try {
    value = localStorage.getItem(localStorageKey);
    value = JSON.parse(value);
  } catch (err) {
    console.error(`Could not read "${localStorageKey}" from LocalStorage!`);
    value = null;
  }

  return value;
}
